/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import React, { useContext, useEffect } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import { FormControl } from '@mui/material';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { NodeType } from '../../../../backendModels/report.model';
import {
  getPreregistrationTupel,
  getTransportLocationTypeTupelsFor,
  OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY,
  OperationalDataTransportLocationRecord,
  PreregistrationKeys,
  TransportLocationPropertyKeys,
  TransportLocationTypeKeys,
} from '../../../../models/genericElements/operationalData/transportLocation';
import { InputState } from '../../../../backendModels/general.model';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import { Draft } from 'immer';
import { isGenericRecordDeletable } from '../../../../models/generic';
import RadioList from '../../../customs/radioList/RadioList';
import {
  changePropertyAndMarkTouched,
  getTouchedClassName,
  handleUntouchedTextFieldUpdates,
} from '../../../../utils/recordWithTouched';
import _ from 'lodash';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import { nonEmptyString } from '../../../../utils/util';

interface DestinationProps {
  readonly nodeType: NodeType;
}

const radioListItems = getTransportLocationTypeTupelsFor([
  TransportLocationTypeKeys.strokeunit,
  TransportLocationTypeKeys.cardiaccatheter,
  TransportLocationTypeKeys.traumacenter,
]);

export default function Destination({ nodeType }: DestinationProps) {
  const { findRecordOrDefault, adaptRecord, missionData, missionDataUpdate, findNodes } = useContext(ReportsAPIContext);

  const record = findRecordOrDefault(
    'generic',
    nodeType,
    OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY
  ) as OperationalDataTransportLocationRecord;

  useEffect(() => {
    if (missionData != null && findNodes(nodeType).length > 0) {
      adaptRecord(
        'generic',
        nodeType,
        (draft: Draft<OperationalDataTransportLocationRecord>, deleteRecord) => {
          draft.inputState = InputState.ENTERED;

          handleUntouchedTextFieldUpdates(
            draft,
            TransportLocationPropertyKeys.transportLocation,
            missionData.transportDestination?.name
          );

          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        },
        OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY
      );
    }
  }, [missionData, findNodes, nodeType, adaptRecord]);

  const destinationName =
    record.inputState === InputState.ENTERED ? (record.values?.transportLocation?.text ?? '') : '';
  const transportLocationType =
    record.inputState === InputState.ENTERED ? (record.values?.transportLocationType?.option ?? null) : null;
  const transportPreregistration =
    record.inputState === InputState.ENTERED ? (record.values?.transportLocationPreregistration?.options ?? []) : [];

  function changePreregistration(value: PreregistrationKeys[]) {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<OperationalDataTransportLocationRecord>) => {
        draft.inputState = InputState.ENTERED;
        if (value.length === 0) {
          delete draft.values?.transportLocationPreregistration;
          delete draft.values?.transportLocationType;
        } else {
          draft.values = {
            ...draft.values,
            transportLocationPreregistration: {
              fieldType: 'multiSelect',
              options: value,
            },
            touched: {
              fieldType: 'multiSelect',
              options: _.union(draft.values?.touched?.options, [
                TransportLocationPropertyKeys.transportLocationPreregistration,
              ]),
            },
          };
        }
      },
      OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY
    );
  }

  function changeTransportLocationType(value: string | null) {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<OperationalDataTransportLocationRecord>) => {
        draft.inputState = InputState.ENTERED;
        if (value == null) {
          draft.values = {
            ...draft.values,
            touched: {
              fieldType: 'multiSelect',
              options: _.union(draft.values?.touched?.options, [TransportLocationPropertyKeys.transportLocationType]),
            },
          };
          delete draft.values?.transportLocationType;
        } else {
          draft.values = {
            ...draft.values,
            transportLocationType: {
              fieldType: 'singleSelect',
              option: value as TransportLocationTypeKeys,
            },
            transportLocationPreregistration: {
              fieldType: 'multiSelect',
              options: [PreregistrationKeys.preregistration],
            },
            touched: {
              fieldType: 'multiSelect',
              options: _.union(draft.values?.touched?.options, [
                TransportLocationPropertyKeys.transportLocationPreregistration,
                TransportLocationPropertyKeys.transportLocationType,
              ]),
            },
          };
        }
      },
      OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY
    );
  }

  const handleTransportDestinationChange = (changedValue: string) => {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY,
      TransportLocationPropertyKeys.transportLocation,
      changedValue === '' ? undefined : { fieldType: 'text', text: changedValue }
    );
  };

  const handleTransportDestinationSave = (valueToSave: string | null) => {
    if (nonEmptyString(valueToSave)) {
      missionDataUpdate?.({
        ...missionData,
        transportDestination: {
          ...missionData!.transportDestination,
          name: valueToSave ?? '',
        },
      });
    } else {
      missionDataUpdate?.({
        ...missionData,
        transportDestination: _.omit(missionData?.transportDestination, ['name']),
      });
    }
  };

  return (
    <DiviFieldCard cardType={CardId.Destination}>
      <DebouncedTextField
        fullWidth
        className={`no-margin-top ${getTouchedClassName(record, TransportLocationPropertyKeys.transportLocation)}`}
        label='Name der Einrichtung'
        value={destinationName}
        onDebounceChange={handleTransportDestinationChange}
        onUnfocus={(value) => handleTransportDestinationSave(value ?? null)}
      />
      <FormControl className='symptoms-list add-margin-top' fullWidth>
        <CheckboxList
          items={[getPreregistrationTupel(PreregistrationKeys.preregistration)]}
          selectedValues={transportPreregistration}
          onValuesChange={changePreregistration}
        />
        <div className='indented'>
          <RadioList items={radioListItems} value={transportLocationType} onChange={changeTransportLocationType} />
        </div>
      </FormControl>
    </DiviFieldCard>
  );
}
